"use client";

import { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { useTheme } from "../../Utils/useTheme";

const locations = [
  { name: "Coimbatore", lat: 11.0168, lon: 76.9558 },
  { name: "Bangalore", lat: 12.9716, lon: 77.5946 },
  { name: "Dubai", lat: 25.2048, lon: 55.2708 },
  { name: "Singapore", lat: 1.3521, lon: 103.8198 },
];

export default function ContactGlobe3D({ activeLocationIndex }) {
  const containerRef = useRef(null);
  const globeGroupRef = useRef(null);
  const targetRotationRef = useRef({ x: 0, y: 0 });
  const { theme } = useTheme();

  const isGreen = theme === "green";
  const isLight = theme === "light";

  const orangeColor = isGreen ? 0x10b981 : (isLight ? 0xea580c : 0xf97316);
  const amberColor = isGreen ? 0x059669 : (isLight ? 0xd97706 : 0xf59e0b);
  const goldColor = isGreen ? 0x34d399 : (isLight ? 0xca8a04 : 0xfbbf24);

  // Convert Lat/Lon to 3D Sphere coordinates
  const convertLatLngToVector3 = (lat, lon, radius) => {
    const phi = (90 - lat) * (Math.PI / 180);
    const theta = (lon + 180) * (Math.PI / 180);

    return new THREE.Vector3(
      -(radius * Math.sin(phi) * Math.sin(theta)),
      radius * Math.cos(phi),
      radius * Math.sin(phi) * Math.cos(theta)
    );
  };

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const width = rect.width || 400;
    const height = rect.height || 400;

    // 1. Scene Setup
    const scene = new THREE.Scene();

    // 2. Camera Setup
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
    camera.position.set(0, 0, 7.5);

    // 3. Renderer Setup
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    renderer.setSize(width, height, false);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.domElement.style.position = "absolute";
    renderer.domElement.style.inset = "0";
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    container.appendChild(renderer.domElement);

    // 4. Orbit Controls (Enable user drag, but smooth lock)
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.enableZoom = false;
    controls.autoRotate = false;

    // 5. Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.3);
    scene.add(ambientLight);

    const dirLight1 = new THREE.DirectionalLight(orangeColor, 2.0); // Orange
    dirLight1.position.set(5, 5, 5);
    scene.add(dirLight1);

    const dirLight2 = new THREE.DirectionalLight(amberColor, 1.2); // Amber
    dirLight2.position.set(-5, -5, -5);
    scene.add(dirLight2);

    // 6. Globe Group
    const globeGroup = new THREE.Group();
    scene.add(globeGroup);
    globeGroupRef.current = globeGroup;

    const radius = 2.5;

    // A. Transparent Inner Sphere Wireframe
    const sphereGeo = new THREE.SphereGeometry(radius, 30, 30);
    const sphereMat = new THREE.MeshBasicMaterial({
      color: orangeColor,
      wireframe: true,
      transparent: true,
      opacity: 0.08,
    });
    const sphereMesh = new THREE.Mesh(sphereGeo, sphereMat);
    globeGroup.add(sphereMesh);

    // B. Point Cloud Earth
    const pointsCount = 900;
    const positions = new Float32Array(pointsCount * 3);
    for (let i = 0; i < pointsCount; i++) {
      const u = Math.random();
      const v = Math.random();
      const theta = u * 2.0 * Math.PI;
      const phi = Math.acos(2.0 * v - 1.0);

      positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = radius * Math.cos(phi);
    }
    const pointsGeo = new THREE.BufferGeometry();
    pointsGeo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    const pointsMat = new THREE.PointsMaterial({
      color: amberColor,
      size: 0.04,
      transparent: true,
      opacity: 0.35,
    });
    const globePoints = new THREE.Points(pointsGeo, pointsMat);
    globeGroup.add(globePoints);

    // C. Plotting Office Locations (Coimbatore, Bangalore, Dubai, Singapore)
    const pinGeo = new THREE.SphereGeometry(0.08, 16, 16);
    const pinMat = new THREE.MeshPhongMaterial({
      color: goldColor, // Gold pin
      emissive: goldColor,
      emissiveIntensity: 0.6,
    });

    const officeVectors = [];

    locations.forEach((loc) => {
      const pos = convertLatLngToVector3(loc.lat, loc.lon, radius);
      officeVectors.push(pos);

      // Node Pin
      const pin = new THREE.Mesh(pinGeo, pinMat);
      pin.position.copy(pos);
      globeGroup.add(pin);

      // Pulsing outer ring
      const ringGeo = new THREE.RingGeometry(0.1, 0.2, 16);
      const ringMat = new THREE.MeshBasicMaterial({
        color: orangeColor,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.6,
      });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.position.copy(pos);
      ring.lookAt(0, 0, 0); // Face center
      globeGroup.add(ring);
    });

    // D. Office Route Connections (Bezier arcs linking Dubai -> Bangalore -> Coimbatore -> Singapore)
    const routeMaterial = new THREE.LineBasicMaterial({
      color: orangeColor,
      transparent: true,
      opacity: 0.4,
    });

    for (let i = 0; i < officeVectors.length - 1; i++) {
      const start = officeVectors[i];
      const end = officeVectors[i + 1];

      const mid = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
      const dist = start.distanceTo(end);
      mid.normalize().multiplyScalar(radius + dist * 0.2); // Elevate arc

      const curve = new THREE.QuadraticBezierCurve3(start, end, mid);
      const points = curve.getPoints(20);
      const routeGeo = new THREE.BufferGeometry().setFromPoints(points);
      const line = new THREE.Line(routeGeo, routeMaterial);
      globeGroup.add(line);
    }

    // 7. Resize Handler
    const handleResize = () => {
      if (!container) return;
      const r = container.getBoundingClientRect();
      const w = r.width || width;
      const h = r.height || height;

      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h, false);
    };
    window.addEventListener("resize", handleResize);

    // 8. Animation Loop
    let animFrameId;
    const animate = () => {
      animFrameId = requestAnimationFrame(animate);

      // Rotate group smoothly towards the target coordinates
      if (globeGroup) {
        globeGroup.rotation.y += (targetRotationRef.current.y - globeGroup.rotation.y) * 0.08;
        globeGroup.rotation.x += (targetRotationRef.current.x - globeGroup.rotation.x) * 0.08;
      }

      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // 9. Cleanup
    return () => {
      cancelAnimationFrame(animFrameId);
      window.removeEventListener("resize", handleResize);
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      
      sphereGeo.dispose();
      sphereMat.dispose();
      pointsGeo.dispose();
      pointsMat.dispose();
      pinGeo.dispose();
      pinMat.dispose();
      routeMaterial.dispose();
      renderer.dispose();
      controls.dispose();
    };
  }, [theme, orangeColor, amberColor, goldColor]);

  // Update target rotation when activeLocationIndex updates
  useEffect(() => {
    if (!globeGroupRef.current) return;

    const loc = locations[activeLocationIndex];
    if (!loc) return;

    // Convert active lat/lon to target Euler rotations for the globe group
    const targetY = -(loc.lon + 180) * (Math.PI / 180) + Math.PI / 2;
    const targetX = (loc.lat) * (Math.PI / 180);

    targetRotationRef.current = {
      x: targetX,
      y: targetY,
    };
  }, [activeLocationIndex]);

  return (
    <div
      ref={containerRef}
      className="w-full h-full min-h-[350px] relative overflow-hidden"
    />
  );
}
